import { useEffect, useState } from 'react'
import { Plus, Edit, Trash2, Gift, Flame, RotateCcw, CalendarClock, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { Campaign, FlashDeal } from '@/modules/promo/model/types'
import { formatVND } from '@/lib/formatters'
import { useAdminPromoStore } from '../../../state/promoStore'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { CampaignFormDialog } from './CampaignFormDialog'
import { DealFormDialog } from './DealFormDialog'
import { useToast } from '../../components/Toast'

type Tab = 'campaigns' | 'deals'

export function AdminPromoPage() {
  const campaigns = useAdminPromoStore((s) => s.campaigns)
  const deals = useAdminPromoStore((s) => s.deals)
  const removeCampaign = useAdminPromoStore((s) => s.removeCampaign)
  const removeDeal = useAdminPromoStore((s) => s.removeDeal)
  const resetAll = useAdminPromoStore((s) => s.resetAll)
  const fetchAll = useAdminPromoStore((s) => s.fetchAll)
  const loading  = useAdminPromoStore((s) => s.loading)
  useEffect(() => { fetchAll() }, [fetchAll])
  const push = useToast((s) => s.push)

  const [tab, setTab] = useState<Tab>('campaigns')

  const [campaignForm, setCampaignForm] = useState<{ open: boolean; editing: Campaign | null }>({ open: false, editing: null })
  const [dealForm, setDealForm] = useState<{ open: boolean; editing: FlashDeal | null }>({ open: false, editing: null })
  const [deleteCampaignId, setDeleteCampaignId] = useState<string | null>(null)
  const [deleteDealId, setDeleteDealId] = useState<string | null>(null)
  const [resetConfirm, setResetConfirm] = useState(false)

  const targetCampaign = deleteCampaignId ? campaigns.find((c) => c.id === deleteCampaignId) : null
  const targetDeal = deleteDealId ? deals.find((d) => d.id === deleteDealId) : null

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">Khuyến mãi</h1>
          <p className="text-sm text-muted mt-1">{campaigns.length} chiến dịch · {deals.length} flash deal</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setResetConfirm(true)}
            className="h-11 px-4 rounded-md border border-border text-text hover:bg-slate-50 font-medium text-sm inline-flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" /> Khôi phục mặc định
          </button>
          <Link
            to="/khuyen-mai"
            target="_blank"
            className="h-11 px-4 rounded-md border border-border text-text hover:bg-slate-50 font-medium text-sm inline-flex items-center gap-2"
          >
            <ExternalLink className="w-4 h-4" /> Xem trang khách
          </Link>
          {tab === 'campaigns' ? (
            <button
              onClick={() => setCampaignForm({ open: true, editing: null })}
              className="h-11 px-5 rounded-md bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Thêm chiến dịch
            </button>
          ) : (
            <button
              onClick={() => setDealForm({ open: true, editing: null })}
              className="h-11 px-5 rounded-md bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Thêm Flash Deal
            </button>
          )}
        </div>
      </div>

      <div className="bg-surface border border-border rounded-xl shadow-card p-2 inline-flex items-center gap-1">
        <button
          onClick={() => setTab('campaigns')}
          className={[
            'h-10 px-5 rounded-md text-sm font-semibold inline-flex items-center gap-2 transition',
            tab === 'campaigns' ? 'bg-brand-600 text-white' : 'text-text hover:bg-slate-50',
          ].join(' ')}
        >
          <Gift className="w-4 h-4" /> Chiến dịch ({campaigns.length})
        </button>
        <button
          onClick={() => setTab('deals')}
          className={[
            'h-10 px-5 rounded-md text-sm font-semibold inline-flex items-center gap-2 transition',
            tab === 'deals' ? 'bg-brand-600 text-white' : 'text-text hover:bg-slate-50',
          ].join(' ')}
        >
          <Flame className="w-4 h-4" /> Flash Sale ({deals.length})
        </button>
      </div>

      {tab === 'campaigns' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {campaigns.length === 0 && (
            <div className="col-span-full bg-surface border border-dashed border-border rounded-xl p-12 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-muted mb-3">
                <Gift className="w-8 h-8" />
              </div>
              <div className="font-semibold text-text">Chưa có chiến dịch nào</div>
              <div className="text-sm text-muted mt-1">Bấm "Thêm chiến dịch" để tạo mới</div>
            </div>
          )}
          {campaigns.map((c) => (
            <div key={c.id} className={`relative rounded-xl overflow-hidden shadow-card bg-gradient-to-br ${c.gradient} text-white group`}>
              <div className="absolute inset-0 opacity-20 overflow-hidden">
                {c.image && <img src={c.image} alt="" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />}
              </div>
              <div className="relative p-5 min-h-[220px] flex flex-col">
                <div className="inline-block bg-white/20 backdrop-blur-sm text-[11px] font-semibold px-2.5 py-1 rounded-full uppercase w-fit">
                  {c.title}
                </div>
                <h3 className="mt-2 text-xl font-bold leading-tight">{c.highlight}</h3>
                <p className="mt-2 text-sm text-white/90 flex-1 line-clamp-3">{c.description}</p>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/20 text-xs">
                  <span className="inline-flex items-center gap-1 text-white/90">
                    <CalendarClock className="w-3.5 h-3.5" /> {c.endDate}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCampaignForm({ open: true, editing: c })}
                      className="w-8 h-8 rounded-md bg-white/15 hover:bg-white/25 flex items-center justify-center transition"
                      aria-label="Sửa"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteCampaignId(c.id)}
                      className="w-8 h-8 rounded-md bg-white/15 hover:bg-red-500 flex items-center justify-center transition"
                      aria-label="Xóa"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'deals' && (
        <div className="bg-surface border border-border rounded-xl shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-muted text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Sản phẩm</th>
                  <th className="text-right px-4 py-3 font-medium">Giá sale</th>
                  <th className="text-right px-4 py-3 font-medium">Giá gốc</th>
                  <th className="text-center px-4 py-3 font-medium w-40">Tiến độ bán</th>
                  <th className="px-4 py-3 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {deals.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-16 text-center">
                      <div className="w-16 h-16 mx-auto rounded-full bg-orange-50 flex items-center justify-center text-orange-600 mb-3">
                        <Flame className="w-8 h-8" />
                      </div>
                      <div className="font-semibold text-text">Chưa có Flash Deal</div>
                      <div className="text-sm text-muted mt-1">Bấm "Thêm Flash Deal" để tạo mới</div>
                    </td>
                  </tr>
                )}
                {deals.map((d) => {
                  const pct = d.stock > 0 ? Math.round((d.sold / d.stock) * 100) : 0
                  const discountPct = d.oldPrice > d.price ? Math.round(((d.oldPrice - d.price) / d.oldPrice) * 100) : 0
                  return (
                    <tr key={d.id} className="border-t border-border hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-md bg-slate-100 overflow-hidden border border-border shrink-0 relative">
                            <img src={d.image} alt={d.name} className="w-full h-full object-cover" onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><rect width="1" height="1" fill="%23e2e8f0"/></svg>'
                            }} />
                            {discountPct > 0 && (
                              <span className="absolute -top-1 -left-1 bg-red-600 text-white text-[9px] font-bold px-1 py-0.5 rounded">-{discountPct}%</span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-text line-clamp-1">{d.name}</div>
                            <div className="text-xs text-muted font-mono">{d.slug}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-red-600">{formatVND(d.price)}</td>
                      <td className="px-4 py-3 text-right text-muted line-through">{formatVND(d.oldPrice)}</td>
                      <td className="px-4 py-3">
                        <div className="h-2 rounded-full bg-orange-100 overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-orange-500 to-red-600" style={{ width: `${Math.min(100, pct)}%` }} />
                        </div>
                        <div className="text-xs text-muted mt-1 text-center">Đã bán {d.sold}/{d.stock} ({pct}%)</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setDealForm({ open: true, editing: d })}
                            className="w-8 h-8 rounded-md hover:bg-brand-50 text-brand-600 flex items-center justify-center transition"
                            aria-label="Sửa"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteDealId(d.id)}
                            className="w-8 h-8 rounded-md hover:bg-red-50 text-danger flex items-center justify-center transition"
                            aria-label="Xóa"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <CampaignFormDialog
        open={campaignForm.open}
        editing={campaignForm.editing}
        onClose={() => setCampaignForm({ open: false, editing: null })}
      />
      <DealFormDialog
        open={dealForm.open}
        editing={dealForm.editing}
        onClose={() => setDealForm({ open: false, editing: null })}
      />

      <ConfirmDialog
        open={!!deleteCampaignId}
        title="Xóa chiến dịch"
        message={`Xóa chiến dịch "${targetCampaign?.title}"? Hành động không thể hoàn tác.`}
        confirmLabel="Xóa"
        onConfirm={async () => {
          if (!deleteCampaignId || !targetCampaign) return
          try { await removeCampaign(deleteCampaignId); push('success', `Đã xóa "${targetCampaign.title}"`) }
          catch (err: any) { push('error', err?.message ?? 'Xóa thất bại') }
          setDeleteCampaignId(null)
        }}
        onCancel={() => setDeleteCampaignId(null)}
      />

      <ConfirmDialog
        open={!!deleteDealId}
        title="Xóa Flash Deal"
        message={`Xóa deal "${targetDeal?.name}"? Hành động không thể hoàn tác.`}
        confirmLabel="Xóa"
        onConfirm={async () => {
          if (!deleteDealId || !targetDeal) return
          try { await removeDeal(deleteDealId); push('success', `Đã xóa "${targetDeal.name}"`) }
          catch (err: any) { push('error', err?.message ?? 'Xóa thất bại') }
          setDeleteDealId(null)
        }}
        onCancel={() => setDeleteDealId(null)}
      />

      <ConfirmDialog
        open={resetConfirm}
        title="Khôi phục mặc định"
        message="Thao tác này sẽ nạp lại toàn bộ chiến dịch và flash deal mặc định, xóa mọi thay đổi hiện tại. Tiếp tục?"
        confirmLabel="Khôi phục"
        tone="brand"
        onConfirm={async () => { await resetAll(); push('info', 'Đã refresh từ server'); setResetConfirm(false) }}
        onCancel={() => setResetConfirm(false)}
      />
    </div>
  )
}
